import { React, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
	Box,
	InputAdornment,
	MenuItem,
	FormControl,
} from "@mui/material";
import {
	useAddWalletMutation,
	useCurrenciesQuery,
	useWalletCategoriesQuery,
	useWalletsQuery,
} from "../../../../../services/queryHooks";
import { Loading } from "../../../../../components/common/Loading";
import { useTranslation } from "react-i18next";
import { returnCategoryTranslation } from "../Functions/returnCategoryTranslation";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { validateTextField } from "../../../../../utils/validation";

export default function AddWalletModal({ isOpen, onClose, type }) {
	const [selectedCurrency, setSelectedCurrency] = useState(
		localStorage.getItem("userCurrency")
	);

	const [name, setName] = useState("");
	const [balance, setBalance] = useState("");
	const [category, setCategory] = useState("");
	const [createdAt, setCreatedAt] = useState(dayjs());
	const [errorName, setErrorName] = useState("");
	const [errorBalance, setErrorBalance] = useState("");
	const [errorCategory, setErrorCategory] = useState("");
	const [errorCreatedAt, setErrorCreatedAt] = useState("");

	const { mutate, isError, error, reset } = useAddWalletMutation();
	const { data: currencies, isPending: currenciesLoading } =
		useCurrenciesQuery();
	const { data: walletCategories, isPending: walletCategoriesLoading } =
		useWalletCategoriesQuery();
	const { data: wallets, isPending: walletsLoading } = useWalletsQuery();
	const isLiability = type === "liability";
	const { t } = useTranslation();
	const userLanguage = localStorage.getItem("i18nextLng");

	const handleBalanceChange = (e) => {
		let value = e.target.value;
		value = value.replace(/[^0-9.,]/g, "");
		value = value.replace(",", ".");
		const regex = /^\d{0,12}(\.\d{0,2})?$/;
		if (!regex.test(value)) return;
		setBalance(value);
	};
	const handleCloseDialog = () => {
		onClose();
		setSelectedCurrency(localStorage.getItem("userCurrency"));
		setBalance("");
		setName("");
		setCategory("");
		setCreatedAt(dayjs());
		setErrorName("");
		setErrorBalance("");
		setErrorCategory("");
		setErrorCreatedAt("");
		reset();
	};

	const minDate = dayjs("2000-01-01");
	const maxDate = dayjs();

	const validateInputs = () => {
		let isValid = true;
		setErrorName("");
		setErrorBalance("");
		setErrorCategory("");
		setErrorCreatedAt("");
		const nameError = validateTextField(name, t, {
			fieldName: t("walletName"),
		});
		if (nameError) {
			isValid = false;
			setErrorName(nameError);
		} else if (
			wallets.some(
				(w) =>
					w.name.toLowerCase() === name.toLowerCase() &&
					w.type === type
			)
		) {
			isValid = false;
			setErrorName(t("walletNameExistsError"));
		} else {
			setErrorName("");
		}
		if (!balance) {
			isValid = false;
			setErrorBalance(t("walletBalanceError"));
		}
		if ((type === "asset" || type === "liability") && !category) {
			isValid = false;
			setErrorCategory(t("walletCategoryError"));
		}
		if (createdAt.isBefore(minDate) || createdAt.isAfter(maxDate)) {
			isValid = false;
			setErrorCreatedAt(t("dateOutOfRange"));
		} else if (!createdAt || !createdAt.isValid()) {
			isValid = false;
			setErrorCreatedAt(t("invalidDate"));
		}
		return isValid;
	};
	const handleSubmit = (e) => {
		e.preventDefault();
		if (errorName || errorBalance || errorCategory) {
			return;
		}
		mutate({
			name: name,
			type: type,
			currency: selectedCurrency,
			balance: isLiability ? -Number(balance) : Number(balance),
			category: category,
			createdAt: createdAt.toISOString(),
		});
		handleCloseDialog();
	};
	if (currenciesLoading || walletCategoriesLoading || walletsLoading) {
		return <Loading />;
	}
	const dialogTitle = () => {
		if (type === "asset") {
			return t("addNewAssetWallet");
		} else if (type === "liability") {
			return t("addNewLiabilityWallet");
		} else if (type === "cash") {
			return t("addNewCashWallet");
		} else {
			return t("addNewWallet");
		}
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleCloseDialog}
				disableEnforceFocus
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{dialogTitle()}</DialogTitle>
				<form noValidate onSubmit={handleSubmit}>
					<DialogContent
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						<TextField
							autoFocus
							type="text"
							label={t("name")}
							value={name}
							onChange={(e) => setName(e.target.value)}
							error={errorName ? true : false}
							helperText={errorName}
						/>
						{(type === "asset" || type === "liability") && (
							<TextField
								select
								label={
									type === "asset"
										? t("assetCategory")
										: t("liabilityCategory")
								}
								error={errorCategory ? true : false}
								helperText={errorCategory}
								value={category}
								onChange={(e) => setCategory(e.target.value)}
							>
								{walletCategories[type].map((category) => (
									<MenuItem key={category} value={category}>
										{returnCategoryTranslation(t, category)}
									</MenuItem>
								))}
							</TextField>
						)}
						<FormControl size="small">
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale={userLanguage}
							>
								<DatePicker
									slotProps={{
										textField: {
											size: "small",
											error: errorCreatedAt !== "",
										},
									}}
									value={createdAt}
									label={t("walletCretionDate")}
									onChange={(newValue) => {
										setCreatedAt(newValue);
									}}
									minDate={minDate}
									maxDate={maxDate}
								/>
							</LocalizationProvider>
						</FormControl>
						<FormControl error={errorBalance ? true : false}>
							<Box sx={{ display: "flex", gap: 1 }}>
								<TextField
									label={t("balance")}
									type="text"
									value={balance}
									error={errorBalance ? true : false}
									helperText={
										<span
											style={{ whiteSpace: "pre-line" }}
										>
											{errorBalance}
										</span>
									}
									slotProps={{
										input: {
											inputMode: "decimal",
											startAdornment: type ===
												"liability" && (
												<InputAdornment position="start">
													-
												</InputAdornment>
											),
										},
									}}
									onChange={handleBalanceChange}
									sx={{ flex: 1 }}
								/>
								<TextField
									select
									value={selectedCurrency}
									onChange={(e) =>
										setSelectedCurrency(e.target.value)
									}
								>
									{currencies.map((currency) => (
										<MenuItem
											key={currency}
											value={currency}
										>
											{currency}
										</MenuItem>
									))}
								</TextField>
							</Box>
						</FormControl>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDialog}>
							{t("cancel")}
						</Button>
						<Button
							type="submit"
							variant="contained"
							onClick={validateInputs}
						>
							{t("save")}
						</Button>
						{isError && <p>Błąd: {error.message}</p>}
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
